import { useCallback, useEffect, useRef, useState } from 'react';
import ProctorService from '../../../utils/ProctorService';
import VisionProctorService from '../../../utils/VisionProctorService';
import AudioProctorService from '../../../utils/AudioProctorService';

const SNAPSHOT_INTERVAL_MS = 15000;

const isFalseVisionStartupEvent = (event = {}) => (
  event.signal === 'vision_unavailable'
  && event.details?.camera?.hasStream === false
  && event.details?.camera?.videoTracks === 0
  && event.details?.modelSource === 'none'
);

export default function useProctor({
  enabled,
  role,
  send,
  videoRef,
  audioStream,
  videoEnabled = true,
  code,
}) {
  const [trustScore, setTrustScore] = useState(100);
  const [events, setEvents] = useState([]);
  const [isProctoring, setIsProctoring] = useState(false);
  const [visionReady, setVisionReady] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [visionUnavailable, setVisionUnavailable] = useState(false);

  const servicesRef = useRef(null);
  const lastCodeRef = useRef(code || '');

  const publishEvent = useCallback((event) => {
    if (isFalseVisionStartupEvent(event)) return;
    setTrustScore(event.trust_score);
    setEvents((current) => {
      const next = [{ ...event, id: `${event.signal}-${event.timestamp}` }, ...current];
      return next.slice(0, 100);
    });

    if (role === 'candidate' && send) {
      send('proctor_event', event);
    }
  }, [role, send]);

  const ingestEvent = useCallback((event) => {
    if (!event?.signal) return;
    if (isFalseVisionStartupEvent(event)) return;
    setTrustScore(event.trust_score ?? 100);
    setEvents((current) => {
      const id = `${event.signal}-${event.timestamp}`;
      if (current.some((item) => item.id === id)) return current;
      return [{ ...event, id }, ...current].slice(0, 100);
    });
  }, []);

  const ingestSnapshot = useCallback((snapshot = {}) => {
    if (typeof snapshot.trustScore === 'number') {
      setTrustScore(snapshot.trustScore);
    }
    if (snapshot.visionReady === false || snapshot.visionUnavailable) {
      setVisionUnavailable(true);
      setVisionReady(false);
    }
    if (snapshot.audioReady) {
      setAudioReady(true);
    }
  }, []);

  const ingestRemoteEvents = useCallback((remoteEvents = []) => {
    const normalized = remoteEvents
      .filter((event) => !isFalseVisionStartupEvent(event))
      .map((event, index) => ({
        ...event,
        id: event.id || `${event.signal}-${event.timestamp || index}`,
      }))
      .reverse();
    setEvents(normalized);
    if (normalized[0]?.trust_score != null) {
      setTrustScore(normalized[0].trust_score);
    }
  }, []);

  const attachEditorDom = useCallback((dom) => {
    servicesRef.current?.core?.attachEditorDom(dom);
  }, []);

  const recordInternalCopy = useCallback((text) => {
    servicesRef.current?.core?.recordInternalCopy(text);
  }, []);

  useEffect(() => {
    if (!enabled || role !== 'candidate') {
      setIsProctoring(false);
      return undefined;
    }

    const core = new ProctorService({ onEvent: publishEvent });
    const vision = new VisionProctorService({
      onEvent: (signal, details) => core.ingestExternalEvent(signal, details),
    });
    const audio = new AudioProctorService({
      onEvent: (signal, details) => core.ingestExternalEvent(signal, details),
    });

    servicesRef.current = { core, vision, audio };
    core.mount();
    setIsProctoring(true);

    const snapshotTimer = window.setInterval(() => {
      send?.('proctor_snapshot', {
        trustScore: core.getTrustScore(),
        visionReady: vision.isReady(),
        audioReady: Boolean(audio.running),
        summary: core.getSummary(),
      });
    }, SNAPSHOT_INTERVAL_MS);

    return () => {
      window.clearInterval(snapshotTimer);
      vision.stop();
      audio.stop();
      core.destroy();
      servicesRef.current = null;
      setIsProctoring(false);
      setVisionReady(false);
      setAudioReady(false);
      setVisionUnavailable(false);
    };
  }, [enabled, publishEvent, role, send]);

  useEffect(() => {
    if (!enabled || role !== 'candidate' || !servicesRef.current?.audio) {
      setAudioReady(false);
      return undefined;
    }
    if (!audioStream) {
      setAudioReady(false);
      return undefined;
    }

    const started = servicesRef.current.audio.start(audioStream);
    setAudioReady(started);

    return () => {
      servicesRef.current?.audio?.stop();
      setAudioReady(false);
    };
  }, [audioStream, enabled, role]);

  useEffect(() => {
    if (!enabled || role !== 'candidate' || !servicesRef.current?.vision) {
      setVisionReady(false);
      setVisionUnavailable(false);
      return undefined;
    }

    const vision = servicesRef.current.vision;
    const core = servicesRef.current.core;
    let cancelled = false;

    const startVision = async () => {
      setVisionReady(false);
      setVisionUnavailable(false);

      if (!videoEnabled || !audioStream) return;

      const videoTracks = typeof audioStream.getVideoTracks === 'function' ? audioStream.getVideoTracks() : [];
      const hasLiveEnabledTrack = videoTracks.some((track) => track.readyState === 'live' && track.enabled);

      if (!videoTracks.length) {
        setVisionUnavailable(true);
        send?.('proctor_snapshot', {
          visionReady: false,
          visionUnavailable: true,
          audioReady: Boolean(audioStream),
          trustScore: core.getTrustScore(),
          message: 'Vision proctoring unavailable because no camera track is available',
        });
        return;
      }

      if (!hasLiveEnabledTrack) return;

      const video = videoRef?.current;
      if (!video) return;
      if (video.srcObject !== audioStream) {
        video.srcObject = audioStream;
      }
      await video.play?.().catch(() => {});
      if (cancelled) return;

      const started = await vision.start(video);
      if (cancelled) return;
      setVisionReady(started);
      setVisionUnavailable(!started);
      if (!started) {
        send?.('proctor_snapshot', {
          visionReady: false,
          visionUnavailable: true,
          audioReady: Boolean(audioStream),
          trustScore: core.getTrustScore(),
          message: 'Vision proctoring unavailable on this device',
        });
      }
    };

    startVision();

    return () => {
      cancelled = true;
      vision.stop();
      setVisionReady(false);
    };
  }, [audioStream, enabled, role, send, videoEnabled, videoRef]);

  useEffect(() => {
    if (!enabled || role !== 'candidate' || !servicesRef.current?.core) return;
    if (code === lastCodeRef.current) return;
    servicesRef.current.core.recordCodeChange(code || '');
    lastCodeRef.current = code || '';
  }, [code, enabled, role]);

  return {
    trustScore,
    events,
    isProctoring,
    visionReady,
    audioReady,
    visionUnavailable,
    attachEditorDom,
    recordInternalCopy,
    ingestEvent,
    ingestSnapshot,
    ingestRemoteEvents,
  };
}
